import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Tooltip,
  Skeleton
} from '@mui/material';
import {
  Edit as EditIcon,
  Lock as LockIcon,
  LockOpen as UnlockIcon
} from '@mui/icons-material';
import { getIconComponent } from '../utils/iconMapping';
import OutputTypeCapabilities from './OutputTypeCapabilities';

const SkeletonRows = ({ count = 4, canManage }) => (
  Array.from({ length: count }).map((_, i) => (
    <TableRow key={i}>
      <TableCell><Skeleton variant="circular" width={24} height={24} /></TableCell>
      <TableCell><Skeleton width={80} /></TableCell>
      <TableCell><Skeleton width={120} /></TableCell>
      <TableCell><Skeleton width={160} /></TableCell>
      <TableCell align="center"><Skeleton width={80} sx={{ mx: 'auto' }} /></TableCell>
      {canManage && <TableCell align="right"><Skeleton variant="circular" width={28} height={28} sx={{ ml: 'auto' }} /></TableCell>}
    </TableRow>
  ))
);

const OutputTypeTable = ({ outputTypes, loading, canManage, onEdit, t }) => {
  return (
    <Paper>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t ? t('QbusOutputTypes.icon') : 'Icon'}</TableCell>
              <TableCell>{t ? t('QbusOutputTypes.name') : 'Name'}</TableCell>
              <TableCell>{t ? t('QbusOutputTypes.friendly_name') : 'Friendly Name'}</TableCell>
              <TableCell>{t ? t('QbusOutputTypes.capabilities') : 'Capabilities'}</TableCell>
              <TableCell align="center">{t ? t('QbusOutputTypes.locked') : 'Locked'}</TableCell>
              {canManage && <TableCell align="right">{t ? t('Common.actions') : 'Actions'}</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <SkeletonRows canManage={canManage} />
            ) : outputTypes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={canManage ? 6 : 5} align="center">
                  <Typography color="text.secondary" sx={{ py: 3 }}>
                    {t ? t('QbusOutputTypes.empty') : 'No output types found'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              outputTypes.map((outputType) => {
                const IconComponent = getIconComponent(outputType.icon);
                return (
                  <TableRow key={outputType.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconComponent color={outputType.isLocked ? 'error' : 'primary'} />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {outputType.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                        {outputType.friendlyName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <OutputTypeCapabilities outputType={outputType} />
                    </TableCell>
                    <TableCell align="center">
                      {outputType.isLocked ? (
                        <Tooltip title={t ? t('QbusOutputTypes.locked_tooltip') : 'Admin-only access'}>
                          <Chip
                            icon={<LockIcon />}
                            label={t ? t('QbusOutputTypes.locked') : 'Locked'}
                            color="error"
                            size="small"
                          />
                        </Tooltip>
                      ) : (
                        <Tooltip title={t ? t('QbusOutputTypes.unlocked_tooltip') : 'Accessible to all users'}>
                          <Chip
                            icon={<UnlockIcon />}
                            label={t ? t('QbusOutputTypes.unlocked') : 'Unlocked'}
                            color="success"
                            size="small"
                            variant="outlined"
                          />
                        </Tooltip>
                      )}
                    </TableCell>
                    {canManage && (
                      <TableCell align="right">
                        <Tooltip title={t ? t('Common.edit') : 'Edit'}>
                          <IconButton size="small" onClick={() => onEdit(outputType)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default OutputTypeTable;
